import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import PageContainer from '../components/layout/PageContainer';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import ScrollReveal from '../components/ui/ScrollReveal';
import Modal from '../components/ui/Modal';
import { MapPin, Globe, Calendar, Send, Clock, EyeOff, User, Edit3, Check, Plane, Bookmark, Award, FileText } from 'lucide-react';
import { TRAVELER_PROFILES } from '../constants/travelerProfiles';
import { IconFromName } from '../utils/iconResolver';
import { listMySavedFiles } from '../api/atlas';
import SendToConcierge from '../components/concierge/SendToConcierge';

/* ─── Skeleton loader ─── */
function ProfileSkeleton() {
  return (
    <div className="max-w-5xl mx-auto animate-pulse">
      <div className="flex flex-col items-center py-10">
        <div className="w-28 h-28 bg-platinum-200 rounded-full mb-5" />
        <div className="h-7 bg-platinum-200 rounded-xl w-48 mb-3" />
        <div className="h-4 bg-platinum-100 rounded w-64 mb-2" />
        <div className="h-4 bg-platinum-100 rounded w-40 mb-5" />
        <div className="flex gap-2">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-7 bg-platinum-100 rounded-full w-24" />
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="bg-white rounded-2xl p-5 text-center shadow-card border border-platinum-200/60">
            <div className="h-8 bg-platinum-200 rounded w-12 mx-auto mb-2" />
            <div className="h-4 bg-platinum-100 rounded w-20 mx-auto" />
          </div>
        ))}
      </div>
      <div className="flex gap-1 mb-8 border-b border-platinum-200 pb-px">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-10 bg-platinum-100 rounded-t-lg w-24" />
        ))}
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[1, 2, 3, 4, 5, 6].map(i => (
          <div key={i} className="bg-white rounded-2xl shadow-card overflow-hidden border border-platinum-200/60">
            <div className="h-40 bg-platinum-200" />
            <div className="p-4">
              <div className="h-5 bg-platinum-200 rounded w-3/4 mb-2" />
              <div className="h-4 bg-platinum-100 rounded w-1/2" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Profile header ─── */
function ProfileHeader({ userName, initials, profile, travelStyleBadges, memberSince, onEditProfile }) {
  const [bioExpanded, setBioExpanded] = useState(false);

  return (
    <ScrollReveal>
      <div className="flex flex-col items-center text-center py-10">
        <div className="w-28 h-28 bg-gradient-to-br from-coral-400 to-coral-500 rounded-full mb-5 flex items-center justify-center text-4xl font-semibold text-white shadow-elevated">
          {initials}
        </div>

        <h1 className="text-3xl font-semibold text-charcoal-500 mb-1">{userName}</h1>

        {profile?.bio && (
          <>
            <p
              className={`text-platinum-600 max-w-md mt-2 leading-relaxed ${!bioExpanded ? 'line-clamp-2 md:line-clamp-none' : ''}`}
            >
              {profile.bio}
            </p>
            {profile.bio.length > 100 && (
              <button
                onClick={() => setBioExpanded(!bioExpanded)}
                className="text-coral-400 text-sm mt-1 md:hidden"
              >
                {bioExpanded ? 'Show less' : 'Show more'}
              </button>
            )}
          </>
        )}

        <div className="flex items-center gap-4 mt-3 text-sm text-platinum-500">
          {profile?.country && (
            <span className="flex items-center gap-1.5">
              <MapPin size={14} className="text-coral-400" />
              {profile.country}
            </span>
          )}
          <span className="flex items-center gap-1.5">
            <Calendar size={14} className="text-coral-400" />
            Joined {memberSince}
          </span>
        </div>

        {travelStyleBadges.length > 0 && (
          <div className="flex flex-wrap gap-2 justify-center mt-5">
            {travelStyleBadges.map(style => (
              <span
                key={style.id}
                className="text-sm px-3 py-1.5 bg-coral-50 text-coral-600 rounded-full font-medium"
                title={style.description}
              >
                <IconFromName name={style.iconName} size={14} className="inline mr-0.5" style={{ color: style.color }} />{style.title.replace('The ', '')}
              </span>
            ))}
          </div>
        )}

        <button
          onClick={onEditProfile}
          className="mt-5 flex items-center gap-1.5 text-sm text-platinum-500 hover:text-coral-500 transition-colors"
        >
          <Edit3 size={14} />
          Edit Profile
        </button>
      </div>
    </ScrollReveal>
  );
}

/* ─── Stats row ─── */
function StatsRow({ totalItineraries, uniqueDestinations, totalDays, publishedCount }) {
  const stats = [
    { value: totalItineraries, label: 'Trips', icon: Calendar, color: 'text-coral-400' },
    { value: uniqueDestinations, label: 'Destinations', icon: MapPin, color: 'text-columbia-500' },
    { value: totalDays, label: 'Days Planned', icon: Clock, color: 'text-naples-500' },
    { value: publishedCount, label: 'Published', icon: Send, color: 'text-green-500' },
  ];

  return (
    <ScrollReveal delay={0.1}>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {stats.map(stat => (
          <div
            key={stat.label}
            className="bg-white rounded-2xl shadow-card border border-platinum-200/60 p-5 text-center"
          >
            <div className={`text-3xl font-semibold ${stat.color}`}>{stat.value}</div>
            <div className="text-sm text-platinum-600 flex items-center justify-center gap-1.5 mt-1.5">
              <stat.icon size={13} />
              {stat.label}
            </div>
          </div>
        ))}
      </div>
    </ScrollReveal>
  );
}

/* ─── Tab bar ─── */
const TABS = [
  { id: 'trips', label: 'Trips', icon: Calendar },
  { id: 'published', label: 'Published', icon: Globe },
  { id: 'saved', label: 'Saved', icon: Bookmark },
  { id: 'about', label: 'About', icon: User },
];

function TabBar({ activeTab, setActiveTab, publishedCount, totalItineraries, savedCount }) {
  return (
    <div className="flex border-b border-platinum-200 mb-8 sticky top-0 bg-white/95 backdrop-blur-sm z-10">
      {TABS.map(tab => (
        <button
          key={tab.id}
          onClick={() => setActiveTab(tab.id)}
          className={`flex items-center gap-2 px-5 py-3 text-sm font-medium transition-colors relative ${
            activeTab === tab.id
              ? 'text-coral-600 border-b-2 border-coral-400'
              : 'text-platinum-500 hover:text-charcoal-500'
          }`}
        >
          <tab.icon size={16} />
          {tab.label}
          {tab.id === 'trips' && totalItineraries > 0 && (
            <span className="text-xs bg-platinum-100 text-platinum-600 px-1.5 py-0.5 rounded-full">
              {totalItineraries}
            </span>
          )}
          {tab.id === 'published' && publishedCount > 0 && (
            <span className="text-xs bg-coral-50 text-coral-500 px-1.5 py-0.5 rounded-full">
              {publishedCount}
            </span>
          )}
          {tab.id === 'saved' && savedCount > 0 && (
            <span className="text-xs bg-coral-50 text-coral-500 px-1.5 py-0.5 rounded-full">
              {savedCount}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

/* ─── Itinerary card ─── */
function ItineraryCard({ itinerary, onUnpublish, showUnpublish = false }) {
  const navigate = useNavigate();

  return (
    <Card hover className="p-0 overflow-hidden flex flex-col h-full">
      <div
        className="h-40 overflow-hidden relative cursor-pointer"
        onClick={() => navigate(`/designer/planner/${itinerary.id}`)}
      >
        {itinerary.thumbnail_url ? (
          <motion.img
            src={itinerary.thumbnail_url}
            alt={itinerary.title}
            className="w-full h-full object-cover"
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.4 }}
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-columbia-200 to-coral-300 flex items-center justify-center">
            <Plane size={40} className="text-white/60" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent pointer-events-none" />
        {itinerary.is_published && (
          <span className="absolute top-3 right-3 text-xs px-2.5 py-1 bg-green-500/90 text-white rounded-full font-medium backdrop-blur-sm">
            Published
          </span>
        )}
      </div>

      <div
        className="flex-1 p-4 cursor-pointer"
        onClick={() => navigate(`/designer/planner/${itinerary.id}`)}
      >
        <h3 className="font-semibold text-charcoal-500 line-clamp-1 mb-1.5">
          {itinerary.title || 'Untitled Trip'}
        </h3>
        <div className="flex items-center gap-3 text-xs text-platinum-600">
          {itinerary.destination && (
            <span className="flex items-center gap-1">
              <MapPin size={12} className="text-coral-400" />
              {itinerary.destination}
            </span>
          )}
          {itinerary.trip_length && (
            <span className="flex items-center gap-1">
              <Clock size={12} />
              {itinerary.trip_length}d
            </span>
          )}
        </div>
      </div>

      {showUnpublish && (
        <div className="px-4 pb-2 pt-0">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onUnpublish(itinerary.id);
            }}
            className="flex items-center gap-1 text-xs text-platinum-500 hover:text-red-500 transition-colors px-2 py-1.5 rounded-lg hover:bg-red-50 w-full justify-center"
          >
            <EyeOff size={12} />
            Unpublish
          </button>
        </div>
      )}

      <div className="px-4 pb-3 pt-0" onClick={(e) => e.stopPropagation()}>
        <SendToConcierge
          itineraryId={itinerary.id}
          tripTitle={itinerary.title}
          destination={itinerary.destination}
          tripLength={itinerary.trip_length}
          source="itinerary"
          compact
          className="flex items-center justify-center gap-1.5 w-full px-3 py-2 text-xs font-semibold text-coral-600 bg-coral-50 hover:bg-coral-100 rounded-lg transition-colors"
        />
      </div>
    </Card>
  );
}

/* ─── Saved Atlas file card (links to the Atlas file, not an itinerary) ─── */
function SavedFileCard({ file }) {
  const navigate = useNavigate();
  const isCurated = file.source_type === 'curated';
  return (
    <Card hover className="p-0 overflow-hidden flex flex-col h-full cursor-pointer" onClick={() => navigate(`/atlas/${file.id}`)}>
      <div className="h-40 overflow-hidden relative">
        {file.cover_image_url ? (
          <motion.img
            src={file.cover_image_url}
            alt={file.title}
            className="w-full h-full object-cover"
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.4 }}
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-columbia-200 to-coral-300 flex items-center justify-center">
            <FileText size={40} className="text-white/60" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent pointer-events-none" />
        {isCurated && (
          <span className="absolute top-3 left-3 flex items-center gap-1 text-xs px-2.5 py-1 bg-columbia-500/90 text-white rounded-full font-medium backdrop-blur-sm">
            <Award size={11} /> Atlas Pick
          </span>
        )}
      </div>
      <div className="flex-1 p-4">
        {file.destination && (
          <p className="text-xs font-semibold uppercase tracking-wide text-platinum-500 mb-0.5">{file.destination}</p>
        )}
        <h3 className="font-semibold text-charcoal-500 line-clamp-2 mb-1.5 leading-snug">
          {file.title || 'Untitled'}
        </h3>
        <div className="flex items-center gap-3 text-xs text-platinum-600">
          {file.author && <span className="flex items-center gap-1"><User size={12} />{file.author}</span>}
          {file.trip_length && <span className="flex items-center gap-1"><Clock size={12} />{file.trip_length}d</span>}
        </div>
      </div>
    </Card>
  );
}

/* ─── Empty state ─── */
function EmptyState({ icon: Icon, title, message, ctaLabel, ctaLink, ctaAction }) {
  return (
    <div className="text-center py-16">
      <div className="w-16 h-16 mx-auto mb-4 bg-platinum-50 rounded-full flex items-center justify-center">
        <Icon size={32} className="text-platinum-400" />
      </div>
      <h3 className="text-xl font-semibold mb-2 text-charcoal-500">{title}</h3>
      <p className="text-platinum-600 max-w-md mx-auto mb-6">{message}</p>
      {ctaLink ? (
        <Link to={ctaLink}>
          <Button>{ctaLabel}</Button>
        </Link>
      ) : ctaAction ? (
        <Button variant="outline" onClick={ctaAction}>{ctaLabel}</Button>
      ) : null}
    </div>
  );
}

/* ─── About tab ─── */
function AboutTab({ profile, memberSince, userEmail, travelStyleBadges }) {
  return (
    <Card>
      <div className="max-w-lg mx-auto py-4 space-y-6">
        {profile?.bio && (
          <div>
            <h4 className="text-sm font-semibold text-platinum-500 uppercase tracking-wider mb-2">About</h4>
            <p className="text-charcoal-500 leading-relaxed">{profile.bio}</p>
          </div>
        )}

        {profile?.country && (
          <div>
            <h4 className="text-sm font-semibold text-platinum-500 uppercase tracking-wider mb-2">Location</h4>
            <p className="text-charcoal-500 flex items-center gap-2">
              <MapPin size={16} className="text-coral-400" />
              {profile.country}
            </p>
          </div>
        )}

        {travelStyleBadges.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-platinum-500 uppercase tracking-wider mb-3">Travel Styles</h4>
            <div className="flex flex-wrap gap-2">
              {travelStyleBadges.map(style => (
                <span
                  key={style.id}
                  className="text-sm px-3 py-1.5 bg-coral-50 text-coral-600 rounded-full font-medium"
                >
                  <IconFromName name={style.iconName} size={14} className="inline mr-0.5" style={{ color: style.color }} />{style.title.replace('The ', '')}
                </span>
              ))}
            </div>
          </div>
        )}

        <div>
          <h4 className="text-sm font-semibold text-platinum-500 uppercase tracking-wider mb-2">Member Since</h4>
          <p className="text-charcoal-500 flex items-center gap-2">
            <Calendar size={16} className="text-coral-400" />
            {memberSince}
          </p>
        </div>

        <div>
          <h4 className="text-sm font-semibold text-platinum-500 uppercase tracking-wider mb-2">Email</h4>
          <p className="text-charcoal-500">{userEmail}</p>
        </div>
      </div>
    </Card>
  );
}

/* ─── Main page ─── */
function ProfilePage() {
  const { user } = useAuth();
  const [itineraries, setItineraries] = useState([]);
  const [savedFiles, setSavedFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [activeTab, setActiveTab] = useState('trips');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState({ bio: '', country: '', travel_style: [] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      Promise.all([fetchItineraries(), fetchProfile(), fetchSaved()]).then(() => setLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchSaved = async () => {
    try {
      const files = await listMySavedFiles();
      setSavedFiles(files || []);
    } catch (err) {
      console.error('Error fetching saved files:', err);
    }
  };

  const fetchItineraries = async () => {
    const { data } = await supabase
      .from('itineraries')
      .select('id, title, destination, trip_length, travel_pace, is_published, thumbnail_url, created_at')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });
    setItineraries(data || []);
  };

  const fetchProfile = async () => {
    const { data } = await supabase
      .from('user_profiles')
      .select('bio, country, travel_style')
      .eq('id', user.id)
      .single();
    if (data) setProfile(data);
  };

  const openEditModal = () => {
    setEditForm({
      bio: profile?.bio || '',
      country: profile?.country || '',
      travel_style: profile?.travel_style || [],
    });
    setEditModalOpen(true);
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    const { error } = await supabase
      .from('user_profiles')
      .update({
        bio: editForm.bio,
        country: editForm.country,
        travel_style: editForm.travel_style,
      })
      .eq('id', user.id);

    if (error) {
      console.error('Error saving profile:', error);
      alert('Failed to save profile');
    } else {
      setProfile(prev => ({ ...prev, ...editForm }));
      setEditModalOpen(false);
    }
    setSaving(false);
  };

  const toggleTravelStyle = (styleId) => {
    setEditForm(prev => ({
      ...prev,
      travel_style: prev.travel_style.includes(styleId)
        ? prev.travel_style.filter(id => id !== styleId)
        : [...prev.travel_style, styleId],
    }));
  };

  const handleUnpublish = async (itineraryId) => {
    const { error } = await supabase
      .from('itineraries')
      .update({ is_published: false })
      .eq('id', itineraryId);

    if (error) {
      console.error('Error unpublishing:', error);
      alert('Failed to unpublish itinerary');
    } else {
      setItineraries(prev =>
        prev.map(it => it.id === itineraryId ? { ...it, is_published: false } : it)
      );
    }
  };

  if (!user) {
    return (
      <PageContainer>
        <div className="text-center py-24">
          <h1 className="text-2xl text-charcoal-500 mb-5">Sign in to view your profile</h1>
          <Link to="/login">
            <Button>Sign In</Button>
          </Link>
        </div>
      </PageContainer>
    );
  }

  if (loading) {
    return (
      <PageContainer>
        <ProfileSkeleton />
      </PageContainer>
    );
  }

  const userName = user.user_metadata?.full_name || 'Travel Enthusiast';
  const userEmail = user.email;
  const memberSince = new Date(user.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const initials = userName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  const totalItineraries = itineraries.length;
  const publishedCount = itineraries.filter(it => it.is_published).length;
  const totalDays = itineraries.reduce((sum, it) => sum + (it.trip_length || 0), 0);
  const uniqueDestinations = new Set(itineraries.map(it => it.destination).filter(Boolean)).size;
  const publishedItineraries = itineraries.filter(it => it.is_published);

  const travelStyleBadges = (profile?.travel_style || [])
    .map(styleId => TRAVELER_PROFILES.find(p => p.id === styleId))
    .filter(Boolean);

  return (
    <PageContainer>
      <div className="max-w-5xl mx-auto">
        <ProfileHeader
          userName={userName}
          initials={initials}
          profile={profile}
          travelStyleBadges={travelStyleBadges}
          memberSince={memberSince}
          onEditProfile={openEditModal}
        />

        <StatsRow
          totalItineraries={totalItineraries}
          uniqueDestinations={uniqueDestinations}
          totalDays={totalDays}
          publishedCount={publishedCount}
        />

        <TabBar
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          publishedCount={publishedCount}
          totalItineraries={totalItineraries}
          savedCount={savedFiles.length}
        />

        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.2 }}
          >
            {activeTab === 'trips' && (
              itineraries.length === 0 ? (
                <EmptyState
                  icon={Plane}
                  title="No trips yet"
                  message="Start planning your next adventure"
                  ctaLabel="Create Your First Trip"
                  ctaLink="/designer/create"
                />
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {itineraries.map((it, index) => (
                    <ScrollReveal key={it.id} delay={index * 0.06}>
                      <ItineraryCard itinerary={it} />
                    </ScrollReveal>
                  ))}
                </div>
              )
            )}

            {activeTab === 'published' && (
              publishedItineraries.length === 0 ? (
                <EmptyState
                  icon={Globe}
                  title="Nothing published yet"
                  message="Share your itineraries with the Travel Atlas community"
                  ctaLabel="Browse Your Trips"
                  ctaAction={() => setActiveTab('trips')}
                />
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {publishedItineraries.map((it, index) => (
                    <ScrollReveal key={it.id} delay={index * 0.06}>
                      <ItineraryCard itinerary={it} onUnpublish={handleUnpublish} showUnpublish />
                    </ScrollReveal>
                  ))}
                </div>
              )
            )}

            {activeTab === 'saved' && (
              savedFiles.length === 0 ? (
                <EmptyState
                  icon={Bookmark}
                  title="No saved itineraries yet"
                  message="Tap the bookmark on any Atlas file to save it here for later"
                  ctaLabel="Browse Atlas Files"
                  ctaLink="/atlas"
                />
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {savedFiles.map((file, index) => (
                    <ScrollReveal key={file.id} delay={index * 0.06}>
                      <SavedFileCard file={file} />
                    </ScrollReveal>
                  ))}
                </div>
              )
            )}

            {activeTab === 'about' && (
              <AboutTab
                profile={profile}
                userEmail={userEmail}
                memberSince={memberSince}
                travelStyleBadges={travelStyleBadges}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Edit Profile Modal */}
      <Modal isOpen={editModalOpen} onClose={() => setEditModalOpen(false)} title="Edit Profile" size="sm">
        <div className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-charcoal-500 mb-1.5">Bio</label>
            <textarea
              value={editForm.bio}
              onChange={(e) => setEditForm(prev => ({ ...prev, bio: e.target.value }))}
              placeholder="Tell us about yourself..."
              rows={3}
              className="w-full rounded-xl border border-platinum-200 px-4 py-2.5 text-sm text-charcoal-500 placeholder:text-platinum-400 focus:outline-none focus:ring-2 focus:ring-coral-300 focus:border-transparent resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-charcoal-500 mb-1.5">Country</label>
            <input
              type="text"
              value={editForm.country}
              onChange={(e) => setEditForm(prev => ({ ...prev, country: e.target.value }))}
              placeholder="Where are you from?"
              className="w-full rounded-xl border border-platinum-200 px-4 py-2.5 text-sm text-charcoal-500 placeholder:text-platinum-400 focus:outline-none focus:ring-2 focus:ring-coral-300 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-charcoal-500 mb-2">Travel Styles</label>
            <div className="flex flex-wrap gap-2">
              {TRAVELER_PROFILES.map(style => {
                const selected = editForm.travel_style.includes(style.id);
                return (
                  <button
                    key={style.id}
                    type="button"
                    onClick={() => toggleTravelStyle(style.id)}
                    className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors flex items-center gap-1 ${
                      selected
                        ? 'bg-coral-500 text-white'
                        : 'bg-platinum-100 text-platinum-600 hover:bg-platinum-200'
                    }`}
                  >
                    <IconFromName name={style.iconName} size={14} className="inline mr-0.5" style={{ color: style.color }} />{style.title.replace('The ', '')}
                    {selected && <Check size={12} />}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <Button onClick={handleSaveProfile} disabled={saving} className="flex-1">
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button variant="outline" onClick={() => setEditModalOpen(false)} className="flex-1">
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    </PageContainer>
  );
}

export default ProfilePage;
